import { useState, useCallback } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Switch } from '@/components/ui'
import { Key, Copy, RefreshCw, Check } from 'lucide-react'
import { toast } from 'sonner'

export function PasswordGenerator() {
  const [password, setPassword] = useState('')
  const [length, setLength] = useState(16)
  const [copied, setCopied] = useState(false)
  const [options, setOptions] = useState({
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: true,
    excludeAmbiguous: false,
  })

  const generatePassword = useCallback(() => {
    let chars = ''

    if (options.lowercase) {
      chars += options.excludeAmbiguous ? 'abcdefghjkmnpqrstuvwxyz' : 'abcdefghijklmnopqrstuvwxyz'
    }
    if (options.uppercase) {
      chars += options.excludeAmbiguous ? 'ABCDEFGHJKMNPQRSTUVWXYZ' : 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    }
    if (options.numbers) {
      chars += options.excludeAmbiguous ? '23456789' : '0123456789'
    }
    if (options.symbols) {
      chars += '!@#$%^&*()_+-=[]{}|;:,.<>?'
    }

    if (!chars) {
      toast.error('Please select at least one character type')
      return
    }

    let result = ''
    const array = new Uint32Array(length)
    crypto.getRandomValues(array)

    for (let i = 0; i < length; i++) {
      result += chars[array[i] % chars.length]
    }

    setPassword(result)
    setCopied(false)
  }, [length, options])

  const copyToClipboard = async () => {
    if (!password) return

    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
      toast.success('Password copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy password')
    }
  }

  const getStrength = () => {
    if (!password) return { label: 'None', color: 'bg-muted', percent: 0 }

    let score = 0
    if (password.length >= 8) score++
    if (password.length >= 12) score++
    if (password.length >= 16) score++
    if (/[a-z]/.test(password)) score++
    if (/[A-Z]/.test(password)) score++
    if (/[0-9]/.test(password)) score++
    if (/[^a-zA-Z0-9]/.test(password)) score++

    if (score <= 2) return { label: 'Weak', color: 'bg-red-500', percent: 25 }
    if (score <= 4) return { label: 'Fair', color: 'bg-yellow-500', percent: 50 }
    if (score <= 6) return { label: 'Strong', color: 'bg-primary', percent: 75 }
    return { label: 'Very Strong', color: 'bg-green-500', percent: 100 }
  }

  const strength = getStrength()

  const updateOption = (key: keyof typeof options, value: boolean) => {
    setOptions((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Key className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Password Generator</h1>
            <p className="text-muted-foreground">Generate secure, random passwords</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Generator */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Generated Password</CardTitle>
            <CardDescription>Click generate to create a new password</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Input
                value={password}
                readOnly
                placeholder="Click generate to create password"
                className="pr-20 font-mono text-lg h-14"
              />
              <div className="absolute right-1 top-1 flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={copyToClipboard}
                  disabled={!password}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={generatePassword}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Strength meter */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Strength</span>
                <span className={password ? 'text-foreground' : 'text-muted-foreground'}>
                  {strength.label}
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${strength.color}`}
                  style={{ width: `${strength.percent}%` }}
                />
              </div>
            </div>

            <Button onClick={generatePassword} className="w-full">
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate Password
            </Button>
          </CardContent>
        </Card>

        {/* Options */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Options</CardTitle>
            <CardDescription>Customize your password settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Length slider */}
            <div className="space-y-3">
              <div className="flex justify-between">
                <Label>Length</Label>
                <span className="text-sm font-mono text-primary">{length}</span>
              </div>
              <input
                type="range"
                min="4"
                max="64"
                value={length}
                onChange={(e) => setLength(Number(e.target.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>4</span>
                <span>64</span>
              </div>
            </div>

            {/* Character options */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Uppercase Letters</Label>
                  <p className="text-xs text-muted-foreground">A-Z</p>
                </div>
                <Switch
                  checked={options.uppercase}
                  onChange={(e) => updateOption('uppercase', e.target.checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Lowercase Letters</Label>
                  <p className="text-xs text-muted-foreground">a-z</p>
                </div>
                <Switch
                  checked={options.lowercase}
                  onChange={(e) => updateOption('lowercase', e.target.checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Numbers</Label>
                  <p className="text-xs text-muted-foreground">0-9</p>
                </div>
                <Switch
                  checked={options.numbers}
                  onChange={(e) => updateOption('numbers', e.target.checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Symbols</Label>
                  <p className="text-xs text-muted-foreground">!@#$%^&*()</p>
                </div>
                <Switch
                  checked={options.symbols}
                  onChange={(e) => updateOption('symbols', e.target.checked)}
                />
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <div>
                  <Label>Exclude Ambiguous</Label>
                  <p className="text-xs text-muted-foreground">Avoid 0, O, l, 1, I</p>
                </div>
                <Switch
                  checked={options.excludeAmbiguous}
                  onChange={(e) => updateOption('excludeAmbiguous', e.target.checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
